// คลิปบอร์ดออบเจกต์ของตัวแก้ PDF/ภาพ เก็บในหน่วยความจำระดับ module (ไม่ใช่ clipboard ระบบ)
// เก็บพิกัดเป็นสัดส่วนของ "หน้าที่ผู้ใช้เห็น" (หลังหมุน) และขนาดเป็นสัดส่วนความกว้างหน้า
// จึงวางลงหน้าอื่นที่ขนาด/การหมุนต่างกัน หรือข้ามระหว่าง PDF กับภาพได้ โดยคงตำแหน่งสัมพัทธ์ตามที่เห็น
import { nativeToVisible, normRot, rotateVec, visibleSize, visibleToNative } from "./geometry";
import { textBox } from "./textUtils";

const isStroke = (o) => o.type === "stroke" || o.type === "highlight";
const OFFSET_STEP = 0.025; // เลื่อนเยื้องต่อครั้งเมื่อวางซ้อนหน้าเดิม (สัดส่วนความกว้างหน้า)

let clip = null; // { items, srcKey, pasteCounts }

export const hasClipboard = () => !!clip && clip.items.length > 0;
export const clipboardCount = () => (clip ? clip.items.length : 0);

// src = { W, H, R } ของหน้าต้นทาง (native กว้าง×สูง + มุมหมุนรวม), srcKey = รหัสหน้าต้นทางที่ไม่ซ้ำข้ามไฟล์
export function copyObjects(objects, src, srcKey) {
  if (!objects.length) return 0;
  const v = visibleSize(src.W, src.H, src.R);
  const items = objects.map((o) => {
    if (isStroke(o)) {
      return {
        type: o.type, color: o.color, fwidth: o.width / v.w,
        points: o.points.map((p) => {
          const q = nativeToVisible(p.x, p.y, src.W, src.H, src.R);
          return { fx: q.x / v.w, fy: q.y / v.h };
        }),
      };
    }
    const q = nativeToVisible(o.x, o.y, src.W, src.H, src.R);
    const { id, x, y, rot, w, h, size, width, strength, ...rest } = o;
    const item = { ...rest, fx: q.x / v.w, fy: q.y / v.h, vrot: normRot((rot || 0) + src.R) };
    if (w !== undefined) { item.fw = w / v.w; item.fh = h / v.w; }
    if (size !== undefined) item.fsize = size / v.w;
    if (width !== undefined) item.fwidth = width / v.w;
    if (strength !== undefined) item.fstrength = strength / v.w;
    return item;
  });
  clip = { items, srcKey, pasteCounts: {} };
  return items.length;
}

// dest = { W, H, R }, destKey = รหัสหน้าปลายทาง, accepts = ชนิดออบเจกต์ที่หน้าปลายทางรองรับ
// คืน [] ถ้าไม่มีของให้วาง
export function pasteObjects(dest, destKey, { accepts, newId }) {
  if (!hasClipboard()) return [];
  const items = clip.items.filter((it) => !accepts || accepts.includes(it.type));
  if (!items.length) return [];

  const dv = visibleSize(dest.W, dest.H, dest.R);
  const count = (clip.pasteCounts[destKey] || 0) + (destKey === clip.srcKey ? 1 : 0);
  const off = count * OFFSET_STEP * dv.w;

  // ขั้นที่ 1: คำนวณในพื้นที่ที่เห็น (visible) พร้อมกรอบล้อมของทั้งกลุ่ม
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const grow = (x, y) => {
    minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
  };

  const placed = items.map((it) => {
    if (isStroke(it)) {
      const width = it.fwidth * dv.w;
      const pts = it.points.map((p) => ({ x: p.fx * dv.w + off, y: p.fy * dv.h + off }));
      pts.forEach((p) => { grow(p.x - width / 2, p.y - width / 2); grow(p.x + width / 2, p.y + width / 2); });
      return { it, pts, width };
    }
    const next = { vx: it.fx * dv.w + off, vy: it.fy * dv.h + off, vrot: it.vrot };
    let w; let h;
    if (it.type === "text") {
      const size = it.fsize * dv.w;
      ({ w, h } = textBox({ text: it.text, size, bold: it.bold }));
      next.size = size;
    } else {
      w = it.fw * dv.w; h = it.fh * dv.w;
      next.w = w; next.h = h;
    }
    [[0, 0], [w, 0], [w, h], [0, h]].forEach(([cx, cy]) => {
      const r = rotateVec(cx, cy, it.vrot);
      grow(next.vx + r.x, next.vy + r.y);
    });
    return { it, next };
  });

  // ขั้นที่ 2: ดันทั้งกลุ่มกลับเข้าหน้า (ถ้ากลุ่มใหญ่กว่าหน้า ชิดซ้าย/บน)
  let sx = 0; let sy = 0;
  if (maxX + sx > dv.w) sx = dv.w - maxX;
  if (minX + sx < 0) sx = -minX;
  if (maxY + sy > dv.h) sy = dv.h - maxY;
  if (minY + sy < 0) sy = -minY;

  clip.pasteCounts[destKey] = (clip.pasteCounts[destKey] || 0) + 1;

  return placed.map((entry) => {
    const { it } = entry;
    if (entry.pts) {
      return {
        type: it.type, color: it.color, width: entry.width, id: newId(),
        points: entry.pts.map((p) => visibleToNative(p.x + sx, p.y + sy, dest.W, dest.H, dest.R)),
      };
    }
    const { fx, fy, vrot, fw, fh, fsize, fwidth, fstrength, ...rest } = it;
    const n = visibleToNative(entry.next.vx + sx, entry.next.vy + sy, dest.W, dest.H, dest.R);
    const obj = { ...rest, id: newId(), x: n.x, y: n.y, rot: normRot(vrot - dest.R) };
    if (entry.next.w !== undefined) { obj.w = entry.next.w; obj.h = entry.next.h; }
    if (entry.next.size !== undefined) obj.size = entry.next.size;
    if (fwidth !== undefined) obj.width = fwidth * dv.w;
    if (fstrength !== undefined) obj.strength = fstrength * dv.w;
    return obj;
  });
}
